/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { atom, type Atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { useAtomCallback } from 'jotai/utils';
import { loadable } from '../utils/loadable';
import { useSetAtom } from 'jotai';
import { IncidentState } from './types';
import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import React, { useCallback } from 'react';

import { fetchFeatureStats, fetchFlatGeobuf, type GeometryVariant } from '../services/featuresApi';
import { dedupeInFlight } from '../services/requestDedupe';
import type { Bbox } from '../map/geometryDetail';

/**
 * Canonical form of an option object: keys sorted and undefined-valued keys
 * dropped at every level, so two spellings of the same request produce one
 * string. Consumers build these inline, so key order and omitted optionals do
 * differ between them. The output stays JSON so paramsAtomFamily can read the
 * params back off the key.
 *
 * Hand-written rather than JSON.stringify with a replacer, because JSON is
 * silently partial in the exact way this keying prevents: NaN, Infinity and an
 * undefined array entry all become null, functions and symbols vanish, and toJSON
 * runs before a replacer sees a Date. Each of those merges two requests into one
 * atom, and one consumer then reads the other's result. Unrepresentable values
 * are rejected rather than collapsed. -0 is the exception: same value, spells "0".
 */
function canonicalParams(value: unknown, path = 'params'): string {
  if (value === null) {
    return 'null';
  }
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return JSON.stringify(value);
    case 'number':
      if (!Number.isFinite(value)) {
        throw new TypeError(
          `Atom family params must be finite numbers: ${path} is ${String(value)}`
        );
      }
      return String(value);
    case 'object':
      break;
    default:
      throw new TypeError(`Atom family params must be JSON data: ${path} is a ${typeof value}`);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry, index) => canonicalParams(entry, `${path}[${index}]`)).join(',')}]`;
  }
  // Anything carrying behaviour or hidden state (a Date, a Map, a class
  // instance) has no faithful JSON form to key on and cannot be read back off
  // the key either.
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(
      `Atom family params must be plain objects: ${path} is ${Object.prototype.toString.call(value)}`
    );
  }
  return `{${Object.entries(value)
    .filter(([, entryValue]) => entryValue !== undefined)
    // Codepoint order, not localeCompare: the key only has to be stable, and a
    // locale-dependent collation makes it depend on where the browser is.
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(
      ([key, entryValue]) =>
        `${JSON.stringify(key)}:${canonicalParams(entryValue, `${path}.${key}`)}`
    )
    .join(',')}}`;
}

type ParamsAtomFamily<Params, AtomType> = {
  (params: Params): AtomType;
  /** The params of every atom the family currently holds. */
  getParams(): Params[];
  remove(params: Params): void;
};

/**
 * An atomFamily keyed by the canonical form of its options, so two spellings of
 * one request share an atom and therefore one query per view load.
 *
 * Keyed by string rather than compared with a comparator because jotai-family
 * answers a value lookup with a Map hit but a comparator with a linear scan over
 * every param it has held -- and these are read during render, one atom per
 * runtime, dozens for a large incident.
 */
function paramsAtomFamily<Params, AtomType extends Atom<unknown>>(
  initializeAtom: (params: Params) => AtomType
): ParamsAtomFamily<Params, AtomType> {
  // The key is JSON, so the params are read back off it for the initializer
  // rather than a second copy of them being kept alongside the family.
  const family = atomFamily((key: string) => initializeAtom(JSON.parse(key) as Params));
  const lookup = (params: Params): AtomType => family(canonicalParams(params));
  return Object.assign(lookup, {
    getParams: (): Params[] => Array.from(family.getParams(), key => JSON.parse(key) as Params),
    remove: (params: Params): void => family.remove(canonicalParams(params)),
  });
}

/**
 * One bar of the county chart: the aggregation row behind it (the grouped column
 * values plus the summed statistics, as the stats endpoint returns them) with the
 * plotted county name and value added. Consumers read the statistic by name, so
 * the row shape is an index signature rather than a fixed set of keys.
 */
export type CountyChartDatum = Record<string, number | string>;

export const countyChartDataAtom = paramsAtomFamily(
  (_param: { incidentId?: string; sectionId?: string }) =>
    atom<{
      labels: string[];
      data: CountyChartDatum[];
      comments?: string[];
      chartDataType: 'sum' | 'percent';
      yAttribute: string;
    } | null>({
      yAttribute: '',
      labels: [],
      data: [],
      chartDataType: 'sum',
    })
);

export type IncidentDataKeys =
  | (typeof atRiskSections)[number]['key']
  | (typeof resourceTrackerSections)[number]['key']
  | (typeof communityLifeSections)[number]['key']
  | 'county';

/** The static national boundary set, shared by every incident view. */
const COUNTY_TABLE = 'usa_census_counties';

const INCIDENT_TABLES: Partial<Record<IncidentDataKeys, string>> = {
  energy: 'incident_energy',
  population: 'incident_population',
  transportation: 'incident_transportation',
  commodities: 'incident_commodities',
  foodWaterShelter: 'incident_food_water_shelter',
  residential: 'incident_residential',
  nonResidentialStructures: 'incident_non_residential',
  declarations: 'incident_declarations',
  communications: 'incident_communications',
  healthMedical: 'incident_health_medical',
  county: COUNTY_TABLE,
};

const sectionIds = [
  'population',
  'commodities',
  'residential',
  'nonResidentialStructures',
  'declarations',
  'communications',
  'energy',
  'transportation',
  'foodWaterShelter',
  'healthMedical',
] as const;

export const allSectionsDataAvailabilitySelector = atomFamily((incidentId: string | undefined) =>
  atom(async get => {
    const dataAvailability: Record<IncidentDataKeys, boolean> = {
      energy: false,
      population: false,
      transportation: false,
      commodities: false,
      foodWaterShelter: false,
      residential: false,
      nonResidentialStructures: false,
      personnel: false,
      declarations: false,
      communications: false,
      commerce: false,
      healthMedical: false,
      county: false,
    };

    if (incidentId == null) {
      return dataAvailability;
    }

    for (const sectionId of sectionIds) {
      const sectionData = await get(
        incidentSectionStats({
          incidentId: incidentId,
          sectionId: sectionId,
        })
      );
      dataAvailability[sectionId] = sectionData.features.length > 0;
    }
    return dataAvailability;
  })
);

/**
 * The county resolutions served whole: `coarse` for national zoom, `simplified`
 * above it. The finer columns are only ever read for a viewport (a whole-table
 * request for them exceeds the API's response budget), so they never reach the
 * session-long collection cache below.
 */
export type CountyGeometryVariant = 'simplified' | 'coarse';

/**
 * The runtime the single county boundary chunk is stored under. The reference
 * table has no lastupdatedon column, so incidentSectionRuntimes answers the
 * county section with this one synthetic runtime.
 */
const COUNTY_BOUNDARY_RUNTIME = 0;

/**
 * The county boundaries at one geometry resolution, for the whole country or for
 * `bbox`.
 *
 * The reference table has no event or lastupdatedon column -- it is a static
 * boundary set -- so it shares none of the per-runtime query building below.
 */
export async function fetchCountyBoundaries(
  variant: GeometryVariant,
  bbox?: Bbox,
  signal?: AbortSignal
): Promise<FeatureCollection> {
  const collection = await fetchFlatGeobuf(COUNTY_TABLE, { geometry: variant, bbox, signal });
  // The county layer (and its stats-label matching) was written against
  // county_name/id property names; alias the reference table's columns.
  return {
    ...collection,
    features: collection.features.map(feature => ({
      ...feature,
      properties: {
        ...feature.properties,
        county_name: feature.properties?.name,
        id: feature.properties?.objectid,
      },
    })),
  };
}

export async function asyncGetIncidentData(
  type: IncidentDataKeys,
  query: string,
  lastUpdatedOn?: number
): Promise<FeatureCollection | null> {
  const table = INCIDENT_TABLES[type];
  if (!table) return null;

  const options: {
    filterField?: string;
    filterValue?: string;
    dateField?: string;
    from?: Date;
    to?: Date;
  } = {
    filterField: 'event',
    filterValue: query,
  };
  if (lastUpdatedOn != null) {
    const d = new Date(lastUpdatedOn);
    options.dateField = 'lastupdatedon';
    options.from = d;
    options.to = d;
  }

  // fetchFlatGeobuf pages through the server's limit internally, so this
  // returns the complete result set for the runtime.
  return fetchFlatGeobuf(table, options);
}

export async function asyncGetIncidentNames(sectionId: IncidentDataKeys, incidentId: string) {
  const table = INCIDENT_TABLES[sectionId];
  if (!table) return { features: [] };

  const fields = supportComments.includes(sectionId)
    ? ['event', 'lastupdatedon', 'comments']
    : ['event', 'lastupdatedon'];

  try {
    // distinct collapses per-feature rows to the unique runtimes; consumers sort
    // them. Deduplicated by request because the model picker and the chunk
    // selector are distinct atoms by design but issue the identical query.
    const fc = await dedupeInFlight(`runtimes:${table}:${incidentId}:${fields.join(',')}`, () =>
      fetchFlatGeobuf(table, {
        filterField: 'event',
        filterValue: incidentId,
        excludeGeometry: true,
        distinct: true,
        fields,
      })
    );

    return fc;
  } catch (_error) {
    return { features: [] };
  }
}

type CountyProperties = {
  county: string;
  NAME: string;
  counties_name: string;
  counties_state_name: string;
  name: string;
  state_name: string;
};

type CountyPropertyFormats =
  | {
      type: 'foodWaterShelter';
      props: {
        counties_name: string;
        counties_state_name: string;
      };
    }
  | { type: 'declarations'; props: { name: string; state_name: string } }
  | { type: 'communications'; props: { name: string; state_name: string } }
  | { type: 'default'; props: { county: string } };

export async function asyncGetIncidentCounties(
  sectionId: IncidentDataKeys,
  incidentId: string
): Promise<FeatureCollection<null, { county: string }>> {
  const table = INCIDENT_TABLES[sectionId];
  if (!table) {
    return { type: 'FeatureCollection', features: [] };
  }

  let fields: string[];
  if (sectionId === 'communications') {
    fields = ['name', 'state_name'];
  } else if (sectionId === 'foodWaterShelter') {
    fields = ['counties_name', 'counties_state_name'];
  } else if (sectionId === 'declarations') {
    fields = ['name', 'state_name'];
  } else if (sectionId === 'commodities') {
    fields = ['county', 'state'];
  } else {
    fields = ['county'];
  }

  function handleCountyFormat(county: CountyPropertyFormats) {
    switch (county.type) {
      case 'foodWaterShelter':
        return `${county.props.counties_name}, ${county.props.counties_state_name}`;
      // PostGIS serves lowercase column names (AGOL served NAME/STATE_NAME).
      case 'declarations':
      case 'communications':
        return `${county.props.name}, ${county.props.state_name}`;
      default:
        return county.props.county;
    }
  }

  try {
    const data = await fetchFlatGeobuf<Geometry, CountyProperties>(table, {
      filterField: 'event',
      filterValue: incidentId,
      excludeGeometry: true,
      distinct: true,
      fields,
    });

    const features = data.features.map(f => ({
      ...f,
      properties: {
        county: handleCountyFormat({
          type: ['foodWaterShelter', 'communications', 'declarations'].includes(sectionId)
            ? (sectionId as CountyPropertyFormats['type'])
            : 'default',
          props: f.properties,
        }),
      },
    }));

    return {
      type: 'FeatureCollection' as const,
      features,
    } as unknown as FeatureCollection<null, { county: string }>;
  } catch (_error) {
    return {
      type: 'FeatureCollection' as const,
      features: [],
    } as unknown as FeatureCollection<null, { county: string }>;
  }
}

export const incidentCountiesSelector = paramsAtomFamily(
  (params: { incidentId: string; sectionId: IncidentDataKeys }) =>
    atom(async () => {
      const data = await asyncGetIncidentCounties(params.sectionId, params.incidentId);

      return data.features.map(feature => feature.properties.county);
    })
);

export type OutputStatistics = {
  statisticType: 'count' | 'sum' | 'min' | 'max' | 'avg' | 'stddev';
  onStatisticField: string;
  outStatisticFieldName: string;
};

type IncidentStats = {
  displayFieldName: string;
  features: {
    attributes: {
      [key: string]: number | string;
    };
  }[];
  fields: {
    name: string;
    type: 'esriFieldTypeOID' | 'esriFieldTypeString' | 'esriFieldTypeDouble';
    alias: string;
  }[];
};

export const extentStateAtom = atom<'idle' | 'zoom-to-extent'>('idle');

export async function asyncGetIncidentStatistics(
  incidentId: string,
  sectionId: IncidentDataKeys,
  groupByFieldsForStatistics: string,
  outputStatistics: OutputStatistics[],
  counties?: string[],
  states?: string[],
  geometry?: { xmin: number; ymin: number; xmax: number; ymax: number }
): Promise<IncidentStats> {
  const table = INCIDENT_TABLES[sectionId];
  if (!table) {
    return { displayFieldName: '', features: [], fields: [] };
  }

  // Sections backed by tables without a county column (e.g. communications) can
  // still respond at state granularity, so derive the states of the selected
  // counties ("<county>, <state>") and send them alongside any explicit states.
  const stateFilter = Array.from(
    new Set([
      ...(states ?? []),
      ...(counties ?? [])
        // County strings are "<county>, <state>"; the state is the second
        // segment (matches groupCountiesByState in ExposureStats).
        .map(county => county.split(',')[1]?.trim())
        .filter((state): state is string => !!state),
    ])
  );

  try {
    // Through the shared helper so this path gets the same response validation
    // as the chart path: a body that is not an array of objects is rejected at
    // the boundary instead of reaching the tiles as rows.
    const rows = await fetchFeatureStats(table, {
      groupBy: groupByFieldsForStatistics.split(','),
      statistics: outputStatistics.map(s => `${s.statisticType}:${s.onStatisticField}`).join(','),
      filterField: 'event',
      filterValue: incidentId,
      bbox: geometry ? [geometry.xmin, geometry.ymin, geometry.xmax, geometry.ymax] : undefined,
      counties,
      states: stateFilter,
    });

    return {
      displayFieldName: '',
      fields: outputStatistics.map(s => ({
        name: s.outStatisticFieldName,
        type: 'esriFieldTypeDouble' as const,
        alias: s.outStatisticFieldName,
      })),
      features: rows.map(row => ({
        attributes: {
          ...row,
          lastupdatedon: new Date(row.lastupdatedon as string).valueOf(),
        } as Record<string, number | string>,
      })),
    };
  } catch (error) {
    // The tiles render their zero state, which is what an aggregation over no
    // rows looks like too, so the failure is logged rather than left invisible.
    console.error(`Error fetching ${sectionId} statistics for ${incidentId}:`, error);
    return { displayFieldName: '', features: [], fields: [] };
  }
}

export const dataChunksAtom = atomFamily((_param: string) =>
  atom<IncidentState>({
    energy: { offset: 0, data: [] },
    population: { offset: 0, data: [] },
    transportation: { offset: 0, data: [] },
    commodities: { offset: 0, data: [] },
    foodWaterShelter: { offset: 0, data: [] },
    residential: { offset: 0, data: [] },
    nonResidentialStructures: { offset: 0, data: [] },
    personnel: { offset: 0, data: [] },
    declarations: { offset: 0, data: [] },
    communications: { offset: 0, data: [] },
    commerce: { offset: 0, data: [] },
    healthMedical: { offset: 0, data: [] },
    county: { offset: 0, data: [] },
  })
);

type IncidentRuntime = {
  comments?: string;
  event: string;
  lastupdatedon: number;
};

export const availableRuntimesAtom = atomFamily((_param: string) => atom<IncidentRuntime[]>([]));

export function getOutputStatisticsBySectionId(
  sectionId: string
): (OutputStatistics & { countyFieldName?: string })[] {
  switch (sectionId) {
    case 'population':
      return [
        {
          statisticType: 'sum',
          onStatisticField: 'ls_night_pop',
          outStatisticFieldName: 'ls_night_pop',
          countyFieldName: 'land_scan_night_pop',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'housingunits',
          outStatisticFieldName: 'housingunits',
          countyFieldName: 'hunits',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'pop65over',
          outStatisticFieldName: 'pop65over',
          countyFieldName: 'age65',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'lep',
          outStatisticFieldName: 'lep',
          countyFieldName: 'sum_lep',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'householdswithfs',
          outStatisticFieldName: 'householdswithfs',
          countyFieldName: 'hfsnap',
        },

        {
          statisticType: 'sum',
          onStatisticField: 'householdswithpa',
          outStatisticFieldName: 'householdswithpa',
          countyFieldName: 'hpa',
        },
      ];
    case 'residential':
      return [
        {
          statisticType: 'sum',
          onStatisticField: 'residential',
          outStatisticFieldName: 'residential',
        },
      ];
    case 'nonResidentialStructures':
      return [
        {
          statisticType: 'sum',
          onStatisticField: 'agriculturalru',
          outStatisticFieldName: 'agriculturalru',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'commercialoff',
          outStatisticFieldName: 'commercialoff',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'exemptgovernm',
          outStatisticFieldName: 'exemptgovernm',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'industrialgen',
          outStatisticFieldName: 'industrialgen',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'commercialret',
          outStatisticFieldName: 'commercialret',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'recreational',
          outStatisticFieldName: 'recreational',
        },
      ];

    case 'communications':
      return [
        {
          statisticType: 'avg',
          onStatisticField: 'percentout',
          outStatisticFieldName: 'percentout',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'cell_sites_out_due_to_power',
          outStatisticFieldName: 'cell_sites_out_due_to_power',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'cell_sites_out_due_to_transpor',
          outStatisticFieldName: 'cell_sites_out_due_to_transpor',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'cell_sites_out_due_to_damage',
          outStatisticFieldName: 'cell_sites_out_due_to_damage',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'populationaffected',
          outStatisticFieldName: 'populationaffected',
        },
      ];

    case 'energy':
      return [
        {
          statisticType: 'sum',
          onStatisticField: 'has_fuel__no_power',
          outStatisticFieldName: 'has_fuel__no_power',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'no_fuel__has_power',
          outStatisticFieldName: 'no_fuel__has_power',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'no_fuel__no_power',
          outStatisticFieldName: 'no_fuel__no_power',
        },
      ];

    case 'foodWaterShelter':
      return [
        {
          statisticType: 'avg',
          onStatisticField: 'sheltercapacitypercent',
          outStatisticFieldName: 'sheltercapacitypercent',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'popinopenshelters',
          outStatisticFieldName: 'popinopenshelters',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'boilwateraffectedpopulation',
          outStatisticFieldName: 'boilwateraffectedpopulation',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'sheltersclosed',
          outStatisticFieldName: 'sheltersclosed',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'sheltersopen',
          outStatisticFieldName: 'sheltersopen',
        },
      ];

    case 'healthMedical':
      return [
        {
          statisticType: 'sum',
          onStatisticField: 'dialysis_center',
          outStatisticFieldName: 'dialysis_center',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'hospitals',
          outStatisticFieldName: 'hospitals',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'nursing_homes',
          outStatisticFieldName: 'nursing_homes',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'urgent_care',
          outStatisticFieldName: 'urgent_care',
        },
      ];

    case 'commodities':
      return [
        {
          statisticType: 'sum',
          onStatisticField: 'vulnerablepop',
          outStatisticFieldName: 'vulnerablepop',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'plasticsheetreqs',
          outStatisticFieldName: 'plasticsheetreqs',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'peopleinneed',
          outStatisticFieldName: 'peopleinneed',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'waterreq',
          outStatisticFieldName: 'waterreq',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'mealreq',
          outStatisticFieldName: 'mealreq',
        },
      ];
    case 'transportation':
      return [
        {
          statisticType: 'sum',
          onStatisticField: 'road_closed',
          outStatisticFieldName: 'road_closed',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'totalnumalerts',
          outStatisticFieldName: 'totalnumalerts',
        },
      ];

    case 'declarations':
      return [
        {
          statisticType: 'avg',
          onStatisticField: 'percent_home_insurance',
          outStatisticFieldName: 'percent_home_insurance',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'is_affected',
          outStatisticFieldName: 'is_affected',
        },
        {
          statisticType: 'sum',
          onStatisticField: 'is_designated',
          outStatisticFieldName: 'is_designated',
        },
      ];

    default:
      return [];
  }
}

export const incidentSectionStats = paramsAtomFamily(
  (params: {
    incidentId?: string;
    sectionId?: string;
    geometry?: { xmin: number; ymin: number; xmax: number; ymax: number };
    counties?: string[];
    states?: string[];
    groupByFieldsForStatistics?: string;
    disabledModels?: string[];
  }) =>
    atom(async () => {
      const {
        incidentId,
        sectionId,
        geometry,
        groupByFieldsForStatistics,
        counties,
        states,
        disabledModels,
      } = params;

      if (!incidentId || !sectionId) {
        return {
          displayFieldName: '',
          features: [],
          fields: [],
        };
      }

      const groupByFields =
        groupByFieldsForStatistics != null
          ? groupByFieldsForStatistics
          : ['population', 'residential', 'nonResidentialStructures'].includes(sectionId)
            ? 'lastupdatedon,comments'
            : 'lastupdatedon';

      try {
        const data = await asyncGetIncidentStatistics(
          incidentId,
          sectionId as IncidentDataKeys,
          groupByFields,
          getOutputStatisticsBySectionId(sectionId),
          counties,
          states,
          geometry
        );

        if (disabledModels != null && disabledModels.length > 0) {
          data.features = data.features.filter(f => {
            const comments = f.attributes.comments as string;
            return disabledModels.includes(comments) === false;
          });
        }

        return data;
      } catch (_error) {
        return {
          displayFieldName: '',
          features: [],
          fields: [],
        };
      }
    })
);

export const refreshTriggerAtom = atom(0);

export const usePeriodicIncidentDataRefresh = () => {
  const setRefreshTrigger = useSetAtom(refreshTriggerAtom);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(currentValue => currentValue + 1);
    }, 900000); // 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [setRefreshTrigger]);
};

export const incidentSectionRuntimes = paramsAtomFamily(
  (params: { incidentId: string; sectionId: IncidentDataKeys; disabledModels?: string[] }) =>
    atom(async () => {
      const { incidentId, sectionId, disabledModels } = params;

      if (sectionId === 'county') {
        return [
          {
            comments: '',
            event: incidentId,
            lastupdatedon: COUNTY_BOUNDARY_RUNTIME,
          },
        ];
      }
      const response = await asyncGetIncidentNames(sectionId, incidentId);
      const availableTimes = response.features
        .map(f => {
          const props = lowercaseObjectKeys((f.properties ?? {}) as Record<string, unknown>) as {
            event: string;
            lastupdatedon: number | string;
            comments: string;
          };
          return {
            ...props,
            lastupdatedon:
              typeof props.lastupdatedon === 'string'
                ? new Date(props.lastupdatedon).valueOf()
                : props.lastupdatedon,
          };
        })
        .filter(f => {
          if (disabledModels == null || disabledModels.length === 0) {
            return true;
          }
          return disabledModels.includes(f.comments) === false;
        })
        .sort((a, b) => {
          if (typeof a.lastupdatedon === 'number' && typeof b.lastupdatedon === 'number') {
            return a.lastupdatedon - b.lastupdatedon;
          }

          return 0;
        });

      return availableTimes as IncidentRuntime[];
    })
);

// Feature properties with every key lowercased. PostGIS serves lowercase column
// names where AGOL served some of them uppercased (LASTUPDATEDON), and rows
// backfilled from AGOL can still carry either spelling.
function lowercaseObjectKeys(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key.toLowerCase(), value])
  );
}

export const incidentRuntimeAtom = paramsAtomFamily(
  (_param: { incidentId: string; sectionId: IncidentDataKeys; lastUpdatedOn?: number }) =>
    atom<{
      lastUpdatedOn: number;
      offset: number;
      type: IncidentDataKeys;
      data: FeatureCollection<Geometry, GeoJsonProperties>[];
    } | null>(null)
);

function nonNullable<T>(value: T): value is NonNullable<T> {
  return value !== null && value !== undefined;
}

export const incidentChunksSelector = paramsAtomFamily(
  (params: { incidentId: string; sectionId: IncidentDataKeys; visible: boolean }) =>
    atom(get => {
      get(refreshTriggerAtom);

      if (!params.visible) {
        return [];
      }

      const availableTimes = get(
        loadable(
          incidentSectionRuntimes({
            incidentId: params.incidentId,
            sectionId: params.sectionId,
          })
        )
      );
      if (availableTimes.state === 'loading' || availableTimes.state === 'hasError') {
        return [];
      }

      const allChunks = availableTimes.data.map(runtime => {
        const { lastupdatedon } = runtime;
        const chunk = get(
          incidentRuntimeAtom({
            incidentId: params.incidentId,
            sectionId: params.sectionId,
            lastUpdatedOn: lastupdatedon,
          })
        );

        return chunk;
      });

      return allChunks.filter(nonNullable);
    })
);

export const selectedRunTimeAtom = atomFamily((_param: string) =>
  atom<{ time: number; comments?: string } | null>(null)
);

const supportComments = ['population', 'residential', 'nonResidentialStructures'];

/**
 * The county boundary set for the session, at one geometry resolution.
 *
 * Static national reference data, but a multi-megabyte pull every view needs, so
 * the store resolves it once and hands the parsed collection to later readers.
 * An atom rather than a module-level promise so the lifetime is the store's: one
 * session in the app, one test in the suite. Keyed by resolution so both variants
 * can be held across a zoom-band crossing; the boundaries never go stale. A
 * failed fetch must not be held -- see useSetCountyBoundaries.
 */
export const countyCollectionSelector = atomFamily((variant: CountyGeometryVariant) =>
  atom(async () => fetchCountyBoundaries(variant))
);

/**
 * The county resolution currently in the chunk store, so a camera change can
 * tell whether the boundaries on screen still match the zoom. Null until the
 * first fetch resolves.
 */
export const countyBoundaryVariantAtom = atom<CountyGeometryVariant | null>(null);

/**
 * Loads the county boundaries at a resolution and puts them on the map.
 *
 * Replaces the chunk rather than appending: counties arrive in one page, and
 * appending a second resolution would draw both sets of outlines. The write lands
 * only once resolved, so what is on screen keeps rendering meanwhile.
 *
 * Rejects on failure, having first dropped the atom holding it -- the selector
 * caches for the store's lifetime, so a retained rejection would answer every
 * later reader -- and leaves the resolution unrecorded so a caller can tell a
 * failed load from a loaded one. Recovery is the caller's job (CountyLayer
 * retries), because one 502 must not cost the session its boundaries.
 *
 * `signal` abandons the load: an aborted one writes nothing, so a camera that has
 * since moved to another band is not handed the resolution it left behind (and the
 * loaded-variant marker not told it arrived, which is what would keep the right
 * one from being requested).
 */
export const useSetCountyBoundaries = () =>
  useAtomCallback(
    useCallback(async (get, set, variant: CountyGeometryVariant, signal?: AbortSignal) => {
      // Captured before the await so only the atom that actually rejected is
      // evicted. Two callers awaiting one rejected promise both land in the catch
      // below, and the first eviction lets the second caller's retry mint a
      // replacement: without this check the later catch would drop that
      // replacement -- a healthy, possibly already-resolved collection -- and the
      // session would pay for the multi-megabyte pull again.
      const collectionAtom = countyCollectionSelector(variant);
      let collection: FeatureCollection;
      try {
        collection = await get(collectionAtom);
      } catch (error) {
        if (countyCollectionSelector(variant) === collectionAtom) {
          countyCollectionSelector.remove(variant);
        }
        throw error;
      }
      if (signal?.aborted) {
        return;
      }
      set(
        incidentRuntimeAtom({
          incidentId: 'county',
          sectionId: 'county',
          lastUpdatedOn: COUNTY_BOUNDARY_RUNTIME,
        }),
        {
          lastUpdatedOn: COUNTY_BOUNDARY_RUNTIME,
          offset: collection.features.length,
          type: 'county',
          data: [collection],
        }
      );
      set(countyBoundaryVariantAtom, variant);
    }, [])
  );

export const useSetIncidentDataChunks = (
  incidentKey: string,
  type: IncidentDataKeys,
  lastUpdatedOn?: number,
  onLoadingFinish?: (...args: unknown[]) => void
) => {
  return useAtomCallback(
    useCallback(
      async (_get, set) => {
        if (lastUpdatedOn == null) {
          return;
        }
        let data: FeatureCollection | null;
        try {
          data = await asyncGetIncidentData(type, incidentKey, lastUpdatedOn);
        } catch (error) {
          // Nothing is written, so the runtime still counts as unloaded and the
          // next mount or runtime selection asks again -- where recording an
          // empty chunk would have looked like a runtime that genuinely exposed
          // nothing and blocked the retry. onLoadingFinish still runs so the
          // section stops showing its spinner.
          console.error(`Error fetching ${type} data for ${incidentKey}:`, error);
          onLoadingFinish?.();
          return;
        }
        // A section with no backing table (asyncGetIncidentData answers null)
        // legitimately has nothing to draw.
        const validData = data ?? { type: 'FeatureCollection' as const, features: [] };
        set(
          incidentRuntimeAtom({
            incidentId: incidentKey,
            sectionId: type,
            lastUpdatedOn,
          }),
          prev => {
            if (prev == null) {
              return {
                lastUpdatedOn,
                offset: validData.features.length,
                type,
                data: [validData],
              };
            }
            return {
              ...prev,
              type,
              data: [...prev.data, validData],
              offset: prev.offset + validData.features.length,
              lastUpdatedOn,
            };
          }
        );
        onLoadingFinish?.();
      },
      [type, incidentKey, lastUpdatedOn, onLoadingFinish]
    )
  );
};

export const atRiskSections = [
  {
    title: 'Population',
    key: 'population',
  },
  {
    title: 'Residential Structures',
    key: 'residential',
  },
  {
    title: 'Non-Residential Structures',
    key: 'nonResidentialStructures',
  },
] as const;

export const communityLifeSections = [
  {
    title: 'Communications',
    key: 'communications',
  },
  {
    title: 'Energy',
    key: 'energy',
  },
  {
    title: 'Transportation',
    key: 'transportation',
  },
  {
    title: 'Commerce',
    key: 'commerce',
  },
  {
    title: 'Food, Water, Shelter',
    key: 'foodWaterShelter',
  },
  {
    title: 'Health & Medical',
    key: 'healthMedical',
  },
] as const;

export const resourceTrackerSections = [
  {
    title: 'Commodities',
    key: 'commodities',
  },
  {
    title: 'Personnel',
    key: 'personnel',
  },
  {
    title: 'Declarations',
    key: 'declarations',
  },
] as const;
